import { FC, useCallback, useEffect, useState } from 'react';
import { Addon, AddonCategory, ShopContent } from '../../../types/cms_shared_types';
import { Conditional } from '../../Common/Conditional';
import { Button } from '../../Common/Button';
import { AddonListActions } from '../../../types/webshop';
import { AddonDetailsModal } from './AddonDetailsModal';

interface AddonCardProps {
  addon: Addon;
  addonCategory: AddonCategory;
  selectedAddons: Addon[];
  selectedAddonsActions: AddonListActions;
  cmsShopContent: ShopContent;
}

export const AddonCard: FC<AddonCardProps> = ({
  addon,
  addonCategory,
  selectedAddons,
  selectedAddonsActions,
  cmsShopContent,
}) => {
  const [isSelected, setIsSelected] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setIsSelected(selectedAddons.filter((p) => p.code === addon.code).length === 1);
  }, [addon.code, selectedAddons]);

  const requiresSwitch = useCallback(() => {
    if (!addonCategory.disallowMultipleAddons) {
      return false;
    }

    const categoryAddonUuids = addonCategory.addons.map((addon) => addon.code);
    return selectedAddons.some((selectedAddon) => categoryAddonUuids.includes(selectedAddon.code));
  }, [addonCategory.addons, addonCategory.disallowMultipleAddons, selectedAddons]);

  const openModal = () => {
    setShowModal(true);
  };

  const addAddon = () => {
    selectedAddonsActions.add(addon);
  };

  const switchToAddon = async () => {
    const selectedProviderAddon = selectedAddons.find(
      (selectedAddon) => selectedAddon.addonProvider.uuid === addon.addonProvider.uuid,
    );

    if (selectedProviderAddon) {
      selectedAddonsActions.replace(selectedProviderAddon, addon);
    } else {
      selectedAddonsActions.add(addon);
    }
  };

  const removeAddon = () => {
    const index = selectedAddons.findIndex((selectedAddon) => selectedAddon.code === addon.code);
    selectedAddonsActions.remove(index);
  };

  const computeBorderStyling = () => {
    return isSelected ? 'border border-sami-green' : ' border-sami-card-border ';
  };

  const className =
    'w-full sm:max-w-[600px] sm:w-[300px] lg:w-[500px] xl:w-[600px] flex flex-col mb-4 px-6 ' +
    `py-6 rounded-lg border ${computeBorderStyling()} ${isSelected ? '' : 'cursor-pointer'} shadow hover:shadow-md`;

  return (
    <div onClick={openModal} className={className}>
      <div>
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row items-center gap-5">
            <div className="block w-10 sm:w-14">{<img src={addon?.addonProvider.imageUrl} />}</div>
            <div>
              <div style={{ hyphens: 'auto' }} className="text-sm font-bold sm:text-lg text-text-dark">
                {addon.name}
              </div>
              {addon.monthlyPriceExclVat && (
                <div className="text-sm font-bold sm:text-lg text-text-gray">
                  {addon.monthlyPriceExclVat} {cmsShopContent.priceWithMonthFormat}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end text-sami-green">
            <Conditional
              condition={isSelected}
              trueRender={
                <>
                  <div className="hidden sm:flex">
                    <Button filled text={cmsShopContent.addonsStep.editAddonButton} onClick={openModal} />
                  </div>
                  <div className="sm:hidden">
                    <Button filled text={cmsShopContent.addonsStep.editAddonButton} onClick={openModal} size="small" />
                  </div>
                </>
              }
              falseRender={
                <>
                  <div className="hidden sm:flex">
                    <Button text={cmsShopContent.addonsStep.readMoreButton} onClick={openModal} hover />
                  </div>
                  <div className="sm:hidden">
                    <Button text={cmsShopContent.addonsStep.readMoreButton} onClick={openModal} hover size="small" />
                  </div>
                </>
              }
            />
          </div>
        </div>
        <div className="pt-4 text-xs sm:text-sm text-text-dark">
          {addon.description ?? addon.addonProvider.description}
        </div>
      </div>
      <AddonDetailsModal
        addon={addon}
        isSelected={isSelected}
        showModal={showModal}
        onAdd={() => {
          addAddon();
          setShowModal(false);
        }}
        onSwitchTo={() => {
          switchToAddon();
          setShowModal(false);
        }}
        onConfirm={() => {
          setShowModal(false);
        }}
        onRemove={() => {
          removeAddon();
          setShowModal(false);
        }}
        cmsShopContent={cmsShopContent}
        setShowModal={setShowModal}
        requiresSwitch={requiresSwitch()}
      />
    </div>
  );
};
